import { useState } from "react";
import { demoTracks } from "../../../mocks/arHub";

export default function DemoPlayer() {
  const [playing, setPlaying] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState(demoTracks[0]);
  const [progress, setProgress] = useState(0);
  const [volume] = useState(80);
  const [requestForm, setRequestForm] = useState(false);
  const [reqData, setReqData] = useState({ genre: "", bpm: "", mood: "" });
  const [reqSent, setReqSent] = useState(false);

  const togglePlay = (id: string) => {
    if (playing === id) {
      setPlaying(null);
    } else {
      setPlaying(id);
      setProgress(0);
      const interval = setInterval(() => {
        setProgress((p) => {
          if (p >= 100) { clearInterval(interval); setPlaying(null); return 0; }
          return p + 1;
        });
      }, 200);
    }
  };

  const handleRequest = () => {
    if (!reqData.genre || !reqData.bpm || !reqData.mood) return;
    setReqSent(true);
    setTimeout(() => { setReqSent(false); setRequestForm(false); setReqData({ genre: "", bpm: "", mood: "" }); }, 2000);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Track List */}
      <div className="flex-1 overflow-y-auto space-y-1">
        {demoTracks.map((track) => (
          <div
            key={track.id}
            onClick={() => setSelectedTrack(track)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all ${
              selectedTrack.id === track.id ? "bg-[#252830] border border-[#3E4155]" : "hover:bg-[#252830]/50 border border-transparent"
            }`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(track.id); }}
              className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-colors cursor-pointer ${
                playing === track.id ? "bg-[#E8375F] text-white" : "bg-[#252830] text-[#8B91A8] hover:bg-[#E8375F]/20"
              }`}
            >
              <i className={`${playing === track.id ? "ri-pause-fill" : "ri-play-fill"} text-sm`}></i>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[#111111] text-xs font-medium truncate">{track.title}</span>
                {track.isNew && (
                  <span className="text-[10px] bg-[#E8375F]/20 text-[#E8375F] px-1.5 py-0.5 rounded-full shrink-0">NEW</span>
                )}
              </div>
              <p className="text-[#5A5E72] text-[10px] truncate">{track.artist} · {track.genre}</p>
              {playing === track.id && (
                <div className="w-full bg-[#1C1F2E] rounded-full h-1 mt-1">
                  <div className="h-1 rounded-full bg-[#E8375F]" style={{ width: `${progress}%`, transition: "width 0.2s" }} />
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="text-[#FF9F43] text-xs font-bold">{track.price.toLocaleString()}만원</div>
              <div className="text-[#5A5E72] text-[10px]">BPM {track.bpm}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Selected Track Detail */}
      <div className="border-t border-[#252830] mt-2 pt-3 px-1">
        <div className="bg-[#252830] rounded-xl p-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="text-[#111111] text-sm font-semibold">{selectedTrack.title}</p>
              <p className="text-[#8B91A8] text-[11px]">{selectedTrack.artist}</p>
            </div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map((s) => (
                <i key={s} className={`ri-star-${s <= Math.floor(selectedTrack.rating) ? "fill" : "line"} text-[#FF9F43] text-xs`}></i>
              ))}
            </div>
          </div>
          <p className="text-[#8B91A8] text-[11px] leading-relaxed mb-2">{selectedTrack.description}</p>
          <div className="flex gap-1.5 flex-wrap">
            {selectedTrack.tags.map((tag) => (
              <span key={tag} className="text-[10px] bg-[#1C1F2E] text-[#8B91A8] px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button className="flex-1 py-1.5 bg-[#E8375F] hover:bg-[#d42e52] text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-download-line mr-1"></i>발주 신청
            </button>
            <button
              onClick={() => setRequestForm(!requestForm)}
              className="flex-1 py-1.5 border border-[#3E4155] text-[#8B91A8] hover:text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1"></i>데모 요청
            </button>
          </div>
        </div>

        {requestForm && (
          <div className="mt-2 bg-[#1C1F2E] rounded-xl p-3 space-y-2">
            <p className="text-[#8B91A8] text-[11px] font-semibold">신규 데모 요청</p>
            {reqSent ? (
              <p className="text-[#00C896] text-xs text-center py-2">
                <i className="ri-check-line mr-1"></i>요청 전송 완료! AI 작곡가가 검토합니다.
              </p>
            ) : (
              <>
                <input value={reqData.genre} onChange={(e) => setReqData((p) => ({ ...p, genre: e.target.value }))}
                  placeholder="장르 (예: Dance Pop)" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72]" />
                <input value={reqData.bpm} onChange={(e) => setReqData((p) => ({ ...p, bpm: e.target.value }))}
                  placeholder="BPM 범위 (예: 120-130)" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72]" />
                <input value={reqData.mood} onChange={(e) => setReqData((p) => ({ ...p, mood: e.target.value }))}
                  placeholder="무드 (예: 청량, 감성)" className="w-full bg-[#252830] text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-[#5A5E72]" />
                <button onClick={handleRequest} className="w-full py-1.5 bg-[#E8375F] text-white text-xs rounded-lg cursor-pointer whitespace-nowrap">요청 전송</button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Volume */}
      <div className="flex items-center gap-2 mt-2 px-1">
        <i className="ri-volume-down-line text-[#5A5E72] text-sm"></i>
        <div className="flex-1 bg-[#252830] rounded-full h-1">
          <div className="h-1 rounded-full bg-[#E8375F]" style={{ width: `${volume}%` }} />
        </div>
        <i className="ri-volume-up-line text-[#5A5E72] text-sm"></i>
      </div>
    </div>
  );
}
